import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Game } from '@game/models/Game';
import { createGame, getCurrentPlayer, getOtherPlayers } from '@game/models/Game';

interface GameState {
  // Game state
  game: Game | null;
  
  // Actions
  startNewGame: (playerNames?: string[], boardSeed?: number) => void;
  setGame: (game: Game) => void;
  loadGame: (gameId: string) => void;
  saveGame: () => void;
  clearGame: () => void;
  
  // Selectors (helpers)
  getCurrentPlayer: () => ReturnType<typeof getCurrentPlayer> | null;
  getOtherPlayers: () => ReturnType<typeof getOtherPlayers> | null;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial state - create a default game for settings persistence
      game: createGame(['Player 1', 'Player 2']),
      
      // Actions
      startNewGame: (playerNames?: string[], boardSeed?: number) => {
        // Preserve settings from current game if it exists
        const currentGame = get().game;
        const settings = currentGame?.settings || {
          pocketSize: 'medium' as const,
          showTargetLine: false,
          timerMode: 'off' as const,
          numPlayers: 2,
        };
        
        // Use numPlayers from settings to generate player names if not provided
        const names = playerNames || Array.from({ length: settings.numPlayers }, (_, i) => `Player ${i + 1}`);
        
        const newGame = createGame(names, boardSeed);
        // Apply preserved settings
        newGame.settings = settings;
        
        set({ game: newGame });
      },
      
      setGame: (game: Game) => {
        // Ensure game has settings field (for backward compatibility)
        if (!game.settings) {
          game.settings = {
            pocketSize: 'medium',
            showTargetLine: false,
            timerMode: 'off',
            numPlayers: game.players?.length || 2,
          };
        } else if (!game.settings.numPlayers) {
          // Add numPlayers to existing settings if missing
          game.settings.numPlayers = game.players?.length || 2;
        }
        set({ game });
      },
      
      loadGame: (gameId: string) => {
        // Load game from localStorage or API
        const savedGames = typeof window !== 'undefined' 
          ? JSON.parse(localStorage.getItem('rollwords-saved-games') || '{}')
          : {};
        
        if (savedGames[gameId]) {
          set({ game: savedGames[gameId] });
        }
      },
      
      saveGame: () => {
        const { game } = get();
        if (!game) return;
        
        // Save to localStorage
        if (typeof window !== 'undefined') {
          const savedGames = JSON.parse(localStorage.getItem('rollwords-saved-games') || '{}');
          savedGames[game.id] = game;
          localStorage.setItem('rollwords-saved-games', JSON.stringify(savedGames));
        }
      },
      
      clearGame: () => {
        set({ game: null });
      },
      
      // Selectors
      getCurrentPlayer: () => {
        const { game } = get();
        return game ? getCurrentPlayer(game) : null;
      },
      
      getOtherPlayers: () => {
        const { game } = get();
        return game ? getOtherPlayers(game) : null;
      },
    }),
    {
      name: 'rollwords-game-storage',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      migrate: (persistedState: any, version: number) => {
        const state = persistedState as GameState;
        
        // Migrate from version 0 (no settings) to version 1 (with settings)
        if (version === 0 && state.game && !state.game.settings) {
          state.game.settings = {
            pocketSize: 'medium',
            showTargetLine: false,
            timerMode: 'off',
            numPlayers: state.game.players?.length || 2,
          };
        } else if (state.game?.settings && !state.game.settings.numPlayers) {
          // Add numPlayers to existing settings if missing
          state.game.settings.numPlayers = state.game.players?.length || 2;
        }
        
        return state;
      },
    }
  )
);
